"use client";

import React, { useCallback, useEffect, useMemo, useState } from "react";

// Status union copied from navbar logic
export type DataStatus = "success" | "partial_success" | "in_progress" | "error" | null;

interface DataStatusDotProps {
  connectionIds?: string | string[] | null;
  className?: string;
  titleOverride?: Partial<Record<NonNullable<DataStatus>, string>>;
}

/**
 * Reusable data status dot that mirrors the navbar logic.
 * Provide one or more connectionIds to check status for those accounts.
 */
export function DataStatusDot({ connectionIds, className = "", titleOverride }: DataStatusDotProps) {
  const [status, setStatus] = useState<DataStatus>(null);

  // Normalize incoming ids to a stable, sorted composite key
  const compositeIds = useMemo(() => {
    if (!connectionIds) return null;
    const arr = Array.isArray(connectionIds) ? connectionIds : [connectionIds];
    const filtered = arr.filter(Boolean);
    if (!filtered.length) return null;
    return filtered.slice().sort().join(",");
  }, [connectionIds]);

  const cacheKey = useMemo(() => (compositeIds ? `ig_fetch_status_${compositeIds}` : null), [compositeIds]);

  const normalizeStatus = useCallback((raw: any): DataStatus => {
    const s = (raw || "").toString().trim().toLowerCase();
    if (!s) return null;
    if (["success", "succeeded", "completed", "done"].includes(s)) return "success";
    if (["partial_success", "partial", "partialsuccess"].includes(s)) return "partial_success";
    if (["in_progress", "processing", "running", "queued", "pending"].includes(s)) return "in_progress";
    if (["error", "failed", "failure", "canceled", "cancelled"].includes(s)) return "error";
    return "error";
  }, []);

  const readCachedStatus = useCallback(() => {
    if (!cacheKey) return null;
    try {
      const raw = localStorage.getItem(cacheKey);
      if (!raw) return null;
      return (JSON.parse(raw) as { status: DataStatus })?.status ?? null;
    } catch {
      return null;
    }
  }, [cacheKey]);

  const writeCachedStatus = useCallback((s: DataStatus) => {
    if (!cacheKey) return;
    try {
      localStorage.setItem(cacheKey, JSON.stringify({ status: s }));
    } catch {}
  }, [cacheKey]);

  const clearCachedStatus = useCallback(() => {
    if (!cacheKey) return;
    try {
      localStorage.removeItem(cacheKey);
    } catch {}
  }, [cacheKey]);

  // Initial load
  useEffect(() => {
    let aborted = false;
    const load = async () => {
      if (!compositeIds) {
        setStatus("error");
        return;
      }
      const cached = readCachedStatus();
      if (cached) {
        setStatus(cached);
        try {
          const resp = await fetch(`/api/instagram/social-fetch-status?connectionIds=${encodeURIComponent(compositeIds)}`, { cache: 'no-store', headers: { 'Accept': 'application/json' } } as RequestInit);
          const json = await resp.json();
          const normalized = normalizeStatus(json?.status);
          if (!aborted) {
            setStatus(normalized);
            writeCachedStatus(normalized);
          }
        } catch {}
        return;
      }

      try {
        const resp = await fetch(`/api/instagram/social-fetch-status?connectionIds=${encodeURIComponent(compositeIds)}`, { cache: 'no-store', headers: { 'Accept': 'application/json' } } as RequestInit);
        const json = await resp.json();
        const normalized = normalizeStatus(json?.status);
        if (!aborted) {
          setStatus(normalized);
          writeCachedStatus(normalized);
        }
      } catch {
        if (!aborted) setStatus("error");
      }
    };
    load();
    return () => { aborted = true; };
  }, [compositeIds, readCachedStatus, writeCachedStatus, normalizeStatus]);

  // Listen to global indexing lifecycle events (same as navbar)
  useEffect(() => {
    const onTriggered = () => {
      clearCachedStatus();
      setStatus("in_progress");
    };
    const onCompleted = (e: Event) => {
      try {
        const detail = (e as CustomEvent).detail || {};
        const normalized = normalizeStatus(detail.status);
        setStatus(normalized);
        writeCachedStatus(normalized);
      } catch {}
    };

    window.addEventListener('ig-index-triggered', onTriggered as any);
    window.addEventListener('ig-index-completed', onCompleted as any);
    return () => {
      window.removeEventListener('ig-index-triggered', onTriggered as any);
      window.removeEventListener('ig-index-completed', onCompleted as any);
    };
  }, [clearCachedStatus, writeCachedStatus, normalizeStatus]);

  // Poll while not definitively successful
  useEffect(() => {
    if (!compositeIds) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const resp = await fetch(`/api/instagram/social-fetch-status?connectionIds=${encodeURIComponent(compositeIds)}`, { cache: 'no-store', headers: { 'Accept': 'application/json' } } as RequestInit);
        const json = await resp.json();
        const normalized = normalizeStatus(json?.status);
        if (!cancelled) {
          setStatus(normalized);
          writeCachedStatus(normalized);
        }
      } catch {}
    };
    const id = setInterval(poll, 5000);
    poll();
    return () => { cancelled = true; clearInterval(id); };
  }, [compositeIds, normalizeStatus, writeCachedStatus]);

  const color =
    status === 'success' ? 'bg-green-500' :
    status === 'partial_success' ? 'bg-yellow-500' :
    status === 'in_progress' ? 'bg-neutral-400 animate-pulse' :
    status === 'error' ? 'bg-red-500' : 'bg-neutral-400 animate-pulse';

  const defaultTitles: Record<string, string> = {
    success: 'Data is connected',
    partial_success: 'Data needs to be updated',
    in_progress: 'Data is loading',
    error: 'Data is not connected',
  };
  const title = status ? (titleOverride?.[status] ?? defaultTitles[status]) : 'Checking data status...';

  return (
    <div className={`flex items-center ${className}`} title={title} aria-label={title}>
      <span className={`inline-block h-2 w-2 rounded-full ${color}`} />
    </div>
  );
}
